<template>
  <li v-if="shouldShow" class="collaboration-advertizer" role="presentation">
    <div
      class="inner"
      role="button"
      tabindex="0"
      @click="openCreateOrganization"
      @keydown.enter.prevent="openCreateOrganization"
      @keydown.space.prevent="openCreateOrganization"
    >
      <div class="header">
        <span class="icon-wrap" aria-hidden="true">
          <TeamIcon class="icon" />
        </span>
      </div>
      <div class="copy">
        <div class="title">Create organization</div>
        <div class="subtitle">Collaborate with your team</div>
      </div>
      <div class="dismiss" @click.stop>
        <BaseButton
          kind="auxiliary"
          type="tertiary"
          tooltip="Dismiss"
          @click="dismiss"
        >
          <template #icon>
            <CloseIcon />
          </template>
        </BaseButton>
      </div>
    </div>
  </li>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useModal } from "vue-final-modal";

import BaseButton from "@/components/base/BaseButton.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import TeamIcon from "@/components/icons/onboarding/TeamIcon.vue";
import * as inject from "@/components/inject";
import CreateOrganizationModal from "@/components/modals/CreateOrganizationModal.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useReleaser } from "@/ref";

export default defineComponent({
  name: "CollaborationAdvertizer",
  components: {
    BaseButton,
    CloseIcon,
    TeamIcon,
  },
  setup() {
    const profile = inject.profile();
    const isMenuExpanded = inject.menu().isExpanded;
    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const dismissed = releaser.add(
      syncState("menu/advertizers/collaboration", false)
    );

    const hasOnlyPersonalOrganization = computed(() => {
      const orgs = profile.value.organizations ?? [];
      const nonPersonal = orgs.filter((o) => !o.personal);
      return nonPersonal.length === 0 && orgs.length > 0;
    });

    const shouldShow = computed(
      () =>
        isMenuExpanded.value &&
        !dismissed.value &&
        hasOnlyPersonalOrganization.value
    );

    const dismiss = () => {
      dismissed.value = true;
    };

    const openCreateOrganization = () => {
      const { open } = useModal({
        component: CreateOrganizationModal,
      });
      void open();
    };

    return {
      shouldShow,
      dismiss,
      openCreateOrganization,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/_variables.scss" as *;
@use "@/styles/_themes.scss" as *;
@use "@/styles/_mixins.scss" as *;

.collaboration-advertizer {
  list-style: none;
  margin: 8px 6px 6px;
  padding: 0;
}

.inner {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-block: 10px;
  padding-inline-end: 20px;
  padding-inline-start: 10px;
  border-radius: $border-radius-m;
  cursor: default;
  text-align: start;
  box-sizing: border-box;

  @include themed() {
    border: 1px solid t($grey-15);
    background: t($grey-03);
  }

  @include transition(background 100ms ease-out);

  &:hover {
    @include themed() {
      background: t($grey-05);
    }
  }

  &:focus-visible {
    outline: 2px solid;
    outline-offset: 2px;

    @include themed() {
      outline-color: t($tertiary-50);
    }
  }
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-wrap {
  flex-shrink: 0;
}

.icon {
  display: block;
  width: 20px;
  height: 20px;
}

.copy {
  min-width: 0;
}

.title {
  @extend %font-caption-medium;

  @include themed() {
    color: t($primary);
  }
}

.subtitle {
  margin-top: 2px;
  @extend %font-small-regular;
  line-height: 1.35;

  @include themed() {
    color: t($grey-50);
  }
}

.dismiss {
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;

  :deep(.base-button) {
    padding: 4px !important;
  }
}
</style>
